// Cost Category Table Component
// BP-10-04: Attorney Frontend

import { useState } from 'react';
import { Plus, Trash2, Edit2, Save, X } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import type { LifeCareCostCategory } from '@/features/attorney/types/attorney';

interface CostCategoryTableProps {
  category: string;
  categoryLabel: string;
  items: LifeCareCostCategory[];
  lifeExpectancy: number;
  onAdd: (item: Omit<LifeCareCostCategory, 'id'>) => void;
  onUpdate: (id: string, item: Partial<LifeCareCostCategory>) => void;
  onRemove: (id: string) => void;
  readOnly?: boolean;
}

interface EditingItem {
  id?: string;
  description: string;
  annualCost: string;
  startAge: string;
  endAge: string;
  frequency: string;
  notes: string;
}

const emptyItem: EditingItem = {
  description: '',
  annualCost: '',
  startAge: '0',
  endAge: '',
  frequency: 'annual',
  notes: '',
};

export function CostCategoryTable({
  category,
  categoryLabel,
  items,
  lifeExpectancy,
  onAdd,
  onUpdate,
  onRemove,
  readOnly = false,
}: CostCategoryTableProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editForm, setEditForm] = useState<EditingItem>(emptyItem);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculateLifetimeCost = (item: LifeCareCostCategory): number => {
    const startAge = item.startAge || 0;
    const endAge = item.endAge || lifeExpectancy;
    const years = Math.max(0, endAge - startAge);
    return item.annualCost * years;
  };

  const categoryTotal = items.reduce(
    (sum, item) => sum + calculateLifetimeCost(item),
    0
  );

  const handleStartEdit = (item: LifeCareCostCategory) => {
    setEditingId(item.id);
    setEditForm({
      id: item.id,
      description: item.description,
      annualCost: item.annualCost.toString(),
      startAge: (item.startAge || 0).toString(),
      endAge: (item.endAge || lifeExpectancy).toString(),
      frequency: item.frequency || 'annual',
      notes: item.notes || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setIsAdding(false);
    setEditForm(emptyItem);
  };

  const handleSaveEdit = () => {
    const annualCost = parseFloat(editForm.annualCost) || 0;
    const startAge = parseInt(editForm.startAge) || 0;
    const endAge = parseInt(editForm.endAge) || lifeExpectancy;

    if (editingId) {
      onUpdate(editingId, {
        description: editForm.description,
        annualCost,
        startAge,
        endAge,
        frequency: editForm.frequency,
        notes: editForm.notes,
      });
    } else if (isAdding) {
      onAdd({
        category,
        description: editForm.description,
        annualCost,
        lifetimeCost: annualCost * (endAge - startAge),
        startAge,
        endAge,
        frequency: editForm.frequency,
        notes: editForm.notes,
      });
    }

    handleCancelEdit();
  };

  const handleStartAdd = () => {
    setIsAdding(true);
    setEditForm({ ...emptyItem, endAge: lifeExpectancy.toString() });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <h4 className="font-medium text-navy">{categoryLabel}</h4>
        <span className="text-sm font-medium text-sage">
          Lifetime: {formatCurrency(categoryTotal)}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
              <th className="px-4 py-2 text-left font-medium">Description</th>
              <th className="px-4 py-2 text-right font-medium">Annual Cost</th>
              <th className="px-4 py-2 text-center font-medium">Age Range</th>
              <th className="px-4 py-2 text-right font-medium">Lifetime Cost</th>
              {!readOnly && (
                <th className="px-4 py-2 text-right font-medium w-24">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                {editingId === item.id ? (
                  <>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={editForm.description}
                        onChange={(e) =>
                          setEditForm({ ...editForm, description: e.target.value })
                        }
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="Description"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={editForm.annualCost}
                        onChange={(e) =>
                          setEditForm({ ...editForm, annualCost: e.target.value })
                        }
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                        placeholder="0"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1 justify-center">
                        <input
                          type="number"
                          value={editForm.startAge}
                          onChange={(e) =>
                            setEditForm({ ...editForm, startAge: e.target.value })
                          }
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                          placeholder="0"
                        />
                        <span className="text-gray-400">-</span>
                        <input
                          type="number"
                          value={editForm.endAge}
                          onChange={(e) =>
                            setEditForm({ ...editForm, endAge: e.target.value })
                          }
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                          placeholder={lifeExpectancy.toString()}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right text-sm text-gray-500">
                      {formatCurrency(
                        (parseFloat(editForm.annualCost) || 0) *
                          ((parseInt(editForm.endAge) || lifeExpectancy) -
                            (parseInt(editForm.startAge) || 0))
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={handleSaveEdit}
                          className="p-1 text-sage hover:bg-sage/10 rounded"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2">
                      <div>
                        <span className="text-sm text-navy">{item.description}</span>
                        {item.notes && (
                          <p className="text-xs text-gray-400 mt-0.5">{item.notes}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right text-sm">
                      {formatCurrency(item.annualCost)}
                    </td>
                    <td className="px-4 py-2 text-center text-sm text-gray-500">
                      {item.startAge || 0} - {item.endAge || lifeExpectancy}
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-medium">
                      {formatCurrency(calculateLifetimeCost(item))}
                    </td>
                    {!readOnly && (
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleStartEdit(item)}
                            className="p-1 text-gray-400 hover:text-navy hover:bg-gray-100 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onRemove(item.id)}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </>
                )}
              </tr>
            ))}

            {/* Add New Row */}
            {isAdding && (
              <tr className="bg-sage/5">
                <td className="px-4 py-2">
                  <input
                    type="text"
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm({ ...editForm, description: e.target.value })
                    }
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="Description"
                    autoFocus
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    type="number"
                    value={editForm.annualCost}
                    onChange={(e) =>
                      setEditForm({ ...editForm, annualCost: e.target.value })
                    }
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                    placeholder="0"
                  />
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1 justify-center">
                    <input
                      type="number"
                      value={editForm.startAge}
                      onChange={(e) =>
                        setEditForm({ ...editForm, startAge: e.target.value })
                      }
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                    />
                    <span className="text-gray-400">-</span>
                    <input
                      type="number"
                      value={editForm.endAge}
                      onChange={(e) =>
                        setEditForm({ ...editForm, endAge: e.target.value })
                      }
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                    />
                  </div>
                </td>
                <td className="px-4 py-2 text-right text-sm text-gray-500">
                  {formatCurrency(
                    (parseFloat(editForm.annualCost) || 0) *
                      ((parseInt(editForm.endAge) || lifeExpectancy) -
                        (parseInt(editForm.startAge) || 0))
                  )}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={handleSaveEdit}
                      className="p-1 text-sage hover:bg-sage/10 rounded"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {/* Empty State */}
            {items.length === 0 && !isAdding && (
              <tr>
                <td
                  colSpan={readOnly ? 4 : 5}
                  className="px-4 py-8 text-center text-sm text-gray-500"
                >
                  No items added yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Button */}
      {!readOnly && !isAdding && (
        <div className="px-4 py-3 border-t border-gray-200">
          <button
            onClick={handleStartAdd}
            className="flex items-center gap-2 text-sm text-sage hover:text-sage-dark"
          >
            <Plus className="w-4 h-4" />
            Add Item
          </button>
        </div>
      )}
    </div>
  );
}

export default CostCategoryTable;

